// panels/EditItemPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import './EditItemPanel.css';

const ITEM_TYPES = [
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'manufactured', label: 'Manufactured Part' },
  { value: 'assembly', label: 'Assembly' },
  { value: 'finished_good', label: 'Finished Good' },
];

const EditItemPanel = ({ 
  node, 
  onUpdateNode, 
  onDeleteNode, 
  onClose,
  isTopLevel = false,
}) => {
  const [itemCode, setItemCode] = useState('');
  const [itemName, setItemName] = useState('');
  const [description, setDescription] = useState('');
  const [itemType, setItemType] = useState('manufactured');
  const [isPhantom, setIsPhantom] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalCode, setOriginalCode] = useState('');

  // Sync state when node changes
  useEffect(() => {
    if (node?.data) {
      setItemCode(node.data.itemCode || '');
      setItemName(node.data.itemName || '');
      setDescription(node.data.description || '');
      setItemType(node.data.itemType || 'manufactured');
      setIsPhantom(node.data.isPhantom || false);
      setOriginalCode(node.data.itemCode || '');
      setHasChanges(false);
    }
  }, [node?.id]);

  const handleCodeChange = useCallback((e) => {
    setItemCode(e.target.value.toUpperCase());
    setHasChanges(true);
  }, []);

  const handleNameChange = useCallback((e) => {
    setItemName(e.target.value);
    setHasChanges(true);
  }, []);

  const handleDescriptionChange = useCallback((e) => {
    setDescription(e.target.value);
    setHasChanges(true);
  }, []);

  const handleTypeChange = useCallback((e) => {
    setItemType(e.target.value);
    setHasChanges(true);
  }, []);

  const handlePhantomChange = useCallback((e) => {
    setIsPhantom(e.target.checked);
    setHasChanges(true);
  }, []);

  const handleApply = useCallback(() => {
    if (!node) return;
    
    if (!itemCode.trim()) {
      alert('Item code is required');
      return;
    }
    
    if (!itemName.trim()) {
      alert('Item name is required');
      return;
    }
    
    onUpdateNode(node.id, {
      itemCode: itemCode.trim(),
      itemName: itemName.trim(),
      description: description.trim(),
      itemType,
      isPhantom,
    });
    setHasChanges(false);
    setOriginalCode(itemCode.trim());
  }, [node, itemCode, itemName, description, itemType, isPhantom, onUpdateNode]);

  const handleDelete = useCallback(() => {
    if (!node) return;
    
    const confirmed = window.confirm(
      `Delete "${node.data.itemCode}" from the canvas?\n\nThis will also remove all connections to/from this item.`
    );
    
    if (confirmed) {
      onDeleteNode(node.id);
    }
  }, [node, onDeleteNode]);

  if (!node) return null;

  const { isPending, isModified } = node.data;
  const isExistingItem = !isPending;
  const isCodeBeingChanged = isExistingItem && itemCode.trim() !== originalCode;

  return (
    <div className="edit-item-panel">
      <div className="panel-header">
        <h3>Edit Item</h3>
        <button className="close-btn" onClick={onClose} title="Close panel">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div className="panel-content">
        {/* Status Badges */}
        <div className="status-badges">
          {isTopLevel && (
            <span className="status-badge top-level">TOP-LEVEL</span>
          )}
          {isPending && (
            <span className="status-badge pending">NEW - UNSAVED</span>
          )}
          {!isPending && isModified && (
            <span className="status-badge modified">MODIFIED</span>
          )}
          {!isPending && !isModified && !isTopLevel && (
            <span className="status-badge existing">EXISTING ITEM</span>
          )}
        </div>

        {/* Warning when changing existing item code */}
        {isCodeBeingChanged && (
          <div className="code-change-warning">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <div>
              <strong>Creating new item</strong>
              <span>Changing the code will create a new item instead of modifying "{originalCode}"</span>
            </div>
          </div>
        )}

        {/* Item Code - Editable */}
        <div className="form-group">
          <label htmlFor="edit-item-code">
            Item Code <span className="required">*</span>
          </label>
          <input
            id="edit-item-code"
            type="text"
            value={itemCode}
            onChange={handleCodeChange}
            placeholder="e.g. PART-001"
          />
        </div>

        {/* Item Name - Editable */}
        <div className="form-group">
          <label htmlFor="edit-item-name">
            Item Name <span className="required">*</span>
          </label>
          <input
            id="edit-item-name"
            type="text"
            value={itemName}
            onChange={handleNameChange}
            placeholder="Enter item name"
          />
        </div>

        {/* Description - Editable */}
        <div className="form-group">
          <label htmlFor="edit-description">
            Description
          </label>
          <textarea
            id="edit-description"
            value={description}
            onChange={handleDescriptionChange}
            placeholder="Enter description (optional)"
            rows={3}
          />
        </div>

        {/* Item Type - Editable */}
        <div className="form-group">
          <label htmlFor="edit-item-type">
            Item Type
          </label>
          <select
            id="edit-item-type"
            value={itemType}
            onChange={handleTypeChange}
          >
            {ITEM_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Is Phantom */}
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={isPhantom}
              onChange={handlePhantomChange}
            />
            <span className="checkbox-text">
              Phantom Item
              <span className="checkbox-hint">
                Components explode through to parent
              </span>
            </span>
          </label>
        </div>

        {/* Apply Button */}
        {hasChanges && (
          <button 
            className="btn-apply"
            onClick={handleApply}
          >
            {isCodeBeingChanged ? 'Create as New Item' : 'Apply Changes'}
          </button>
        )}

        {/* Info note */}
        <div className="info-note">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>
            {isExistingItem 
              ? 'Change the item code to create a new item. Other changes will modify the existing item.'
              : 'Changes are saved locally until you click "Save Changes" in the header.'}
          </span>
        </div>

        {/* Delete Button */}
        <button 
          className="btn-delete"
          onClick={handleDelete}
          title="Remove from canvas"
        >
          Remove from Canvas
        </button>
      </div>
    </div>
  );
};

export default EditItemPanel;